import { useContext } from "react";
import { UNSAFE_RouteContext } from "react-router-dom";

/**
 * Returns the current route's pathname WITHOUT its matched splat segment(s) —
 * the parent path a relative redirect *should* resolve against.
 *
 * react-router v7 permanently adopted the former `v7_relativeSplatPath`
 * behavior: `getResolveToMatches` feeds the leaf match's FULL `pathname`
 * (splat value included) into relative-path resolution instead of its
 * `pathnameBase`. A relative `<Navigate to="x" />` or `navigate("x")` whose
 * leaf route is a splat therefore APPENDS to the matched path instead of
 * replacing the splat — which loops forever inside a `path: "*"` catch-all.
 * There is no flag to opt out.
 *
 * This reads the current match's `pathnameBase`, which is exactly the value v7
 * stopped using. Deliberately NOT recomputed from `useParams()["*"]` and
 * `useLocation().pathname`: the splat param is percent-decoded (`%2F` becomes a
 * literal `/`), so `foo%2Fbar` and a genuine two-segment `foo/bar` are
 * indistinguishable in the param and the segment count cannot be recovered from
 * it. react-router slices `pathnameBase` from the raw matched pathname before
 * that decode, so it stays correct for encoded slashes.
 *
 * Do NOT "clean this up" by swapping to the public `useMatches()`: its `UIMatch`
 * exposes only `{id, pathname, params, data, handle}` — no `pathnameBase` — so
 * the only value it can offer is the already-decoded `pathname`, which
 * reintroduces the `%2F` bug. `UNSAFE_RouteContext` is the sole route to the
 * pre-decode value. `react-router-dom` is pinned exact in the root
 * `package.json`, so this internal cannot shift without a deliberate bump.
 *
 * ONLY call this from a route whose own `path` is a splat (`"*"` or ending in
 * `/*`), or from a component used as such a route's `element`. On a non-splat
 * leaf `pathnameBase === pathname`, so appending to the result reproduces the
 * exact bug this hook exists to remove — see the development-only guard below.
 */
export const useSplatBasePath = (): string => {
  const { matches } = useContext(UNSAFE_RouteContext);
  const match = matches[matches.length - 1];
  const base = match?.pathnameBase ?? "";
  const leafPath = match?.route.path;

  // Misuse guard. Both this hook and `SplatRedirect` are barrel-exported from
  // `@dealer-center/core-custom-components`, so a non-splat call site is
  // reachable from anywhere — and it fails SILENTLY, returning the current
  // pathname so that `${base}/${to}` appends exactly as the unfixed v7 bug did.
  //
  // Only a leaf with a NON-EMPTY, NON-SPLAT path is unsafe. A pathless leaf
  // (index route, or a layout with no `path`) takes its `pathnameBase` from the
  // nearest path-contributing ancestor, with that ancestor's splat value already
  // stripped — so it returns a correct parent and must NOT be flagged.
  //
  // Dev-only: `process.env.NODE_ENV` is statically replaced at build time (dot
  // access is required for that substitution), so webpack drops this whole block
  // from production bundles.
  if (
    process.env.NODE_ENV !== "production" &&
    leafPath &&
    !leafPath.endsWith("*")
  ) {
    // eslint-disable-next-line no-console
    console.error(
      `useSplatBasePath: route "${leafPath}" is not a splat, so the returned base is ` +
        `the current pathname ("${base}") rather than a parent. Appending to it will ` +
        `grow the URL instead of replacing a splat. Use a plain relative ` +
        `<Navigate>/navigate() here, and reserve SplatRedirect/useSplatBasePath for ` +
        `routes whose own path is "*" or ends in "/*".`
    );
  }

  // A root-level splat yields "/", which would double up when a caller appends
  // "/<child>". Every other base is already trailing-slash free.
  return base === "/" ? "" : base;
};
